import { useBoxBuilder } from "@/lib/store";
import type { BoxItem } from "@/lib/types";
import { cn } from "@/lib/cn";

export default function ItemCard({
  item,
  atLimit,
}: {
  item: BoxItem;
  atLimit: boolean;
}) {
  const quantity = useBoxBuilder((s) => s.selectedItems[item.id] ?? 0);
  const addItem = useBoxBuilder((s) => s.addItem);
  const removeItem = useBoxBuilder((s) => s.removeItem);

  const outOfStock = item.stock <= 0;
  const atStockCap = quantity >= item.stock;
  const canAdd = !outOfStock && !atLimit && !atStockCap;

  const selected = quantity > 0;

  return (
    <div
      className={cn(
        "card group relative flex flex-col overflow-hidden p-3 transition",
        selected && "ring-2 ring-brand-500",
        outOfStock && "opacity-60",
      )}
    >
      <div className="relative aspect-square w-full overflow-hidden rounded-xl bg-ink-50">
        {item.image ? (
          <img
            src={item.image}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover transition group-hover:scale-[1.02]"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-4xl font-display text-ink-200">
            {item.title.charAt(0)}
          </div>
        )}
        {outOfStock ? (
          <span className="absolute left-2 top-2 rounded-full bg-ink-800 px-2.5 py-1 text-xs font-semibold uppercase tracking-wider text-white">
            Sold out
          </span>
        ) : null}
      </div>
      <div className="flex flex-1 flex-col justify-between pt-4">
        <div>
          <div className="text-base font-semibold text-ink-800 line-clamp-2 sm:text-lg">
            {item.title}
          </div>
          {item.category ? (
            <div className="mt-1 text-xs font-semibold uppercase tracking-[0.12em] text-ink-400">
              {item.category}
            </div>
          ) : null}
        </div>

        <div className="mt-4">
          {selected ? (
            <div className="inline-flex items-center rounded-full bg-ink-800 p-1 text-white">
              <button
                type="button"
                onClick={() => removeItem(item.id)}
                className="flex h-9 w-9 items-center justify-center rounded-full text-lg transition hover:bg-ink-700"
                aria-label={`Remove one ${item.title}`}
              >
                &#8722;
              </button>
              <span className="min-w-[2ch] text-center text-base font-semibold">
                {quantity}
              </span>
              <button
                type="button"
                onClick={() => addItem(item.id)}
                disabled={!canAdd}
                className="flex h-9 w-9 items-center justify-center rounded-full text-lg transition hover:bg-ink-700 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label={`Add another ${item.title}`}
              >
                &#43;
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => addItem(item.id)}
              disabled={!canAdd}
              className={cn(
                "btn w-full",
                canAdd
                  ? "bg-brand-500 text-white shadow-sm hover:bg-brand-600"
                  : "bg-ink-100 text-ink-400",
              )}
            >
              {outOfStock ? "Sold out" : atLimit ? "Box full" : "Add"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
